import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ResponseStorageService, ValidatedResponse } from '../services/response-storage';
import { Download, FileText, FileCode, BarChart3, Clock, CheckCircle, XCircle } from 'lucide-react';

interface ResponseExporterProps {
  sessionId: string;
}

export function ResponseExporter({ sessionId }: ResponseExporterProps) {
  const [responses, setResponses] = useState<ValidatedResponse[]>([]);
  const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0, validPercentage: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadResponses();
  }, [sessionId]);

  const loadResponses = async () => {
    setLoading(true);
    try {
      const [responsesData, statsData] = await Promise.all([
        ResponseStorageService.getResponses(sessionId),
        ResponseStorageService.getResponseStats(sessionId)
      ]);
      
      setResponses(responsesData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportText = async () => {
    setLoading(true);
    try {
      await ResponseStorageService.exportAsText(sessionId);
    } catch (error) {
      console.error('Error exporting text:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportHtml = async () => {
    setLoading(true);
    try {
      await ResponseStorageService.exportAsHtml(sessionId);
    } catch (error) {
      console.error('Error exporting HTML:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const validResponses = responses.filter(r => r.isValid);
  const invalidResponses = responses.filter(r => !r.isValid);

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Export des Réponses
          </h2>
          <Button
            onClick={loadResponses}
            disabled={loading}
            variant="outline"
            size="sm"
          >
            Actualiser
          </Button>
        </div>

        {loading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            <p className="mt-2 text-sm text-gray-600">Chargement...</p>
          </div>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-gray-600">Total</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">{stats.valid}</div>
            <div className="text-sm text-gray-600">Validées</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-600">{stats.invalid}</div>
            <div className="text-sm text-gray-600">Refusées</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.validPercentage}%</div>
            <div className="text-sm text-gray-600">Taux de validation</div>
          </div>
        </div>

        {/* Export Buttons */}
        <div className="flex gap-4 mb-6">
          <Button
            onClick={handleExportText}
            disabled={loading || responses.length === 0}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Exporter en TXT
          </Button>
          <Button
            onClick={handleExportHtml}
            disabled={loading || responses.length === 0}
            variant="outline"
            className="flex items-center gap-2"
          >
            <FileCode className="h-4 w-4" />
            Exporter en HTML
          </Button>
        </div>

        {responses.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucune réponse enregistrée pour cette session.</p>
            <p className="text-sm">Les réponses seront sauvegardées lors de la validation par le maître.</p>
          </div>
        )}

        {/* Responses List */}
        {responses.length > 0 && (
          <div className="space-y-4">
            {validResponses.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Réponses Validées ({validResponses.length})
                </h3>
                <div className="space-y-3">
                  {validResponses.map((response) => (
                    <div key={response.id} className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-green-800">{response.teamName}</div>
                          <div className="text-sm text-gray-600 mb-2">
                            Défi: {response.challenge.trigger || 'N/A'} + {response.challenge.category || 'N/A'}
                          </div>
                          <div className="text-gray-800 mb-2">{response.response}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(response.timestamp)}
                          </div>
                        </div>
                        <CheckCircle className="h-5 w-5 text-green-600 ml-4" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {invalidResponses.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  Réponses Refusées ({invalidResponses.length})
                </h3>
                <div className="space-y-3">
                  {invalidResponses.map((response) => (
                    <div key={response.id} className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-red-800">{response.teamName}</div>
                          <div className="text-sm text-gray-600 mb-2">
                            Défi: {response.challenge.trigger || 'N/A'} + {response.challenge.category || 'N/A'}
                          </div>
                          <div className="text-gray-800 mb-2">{response.response}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(response.timestamp)}
                          </div>
                        </div>
                        <XCircle className="h-5 w-5 text-red-600 ml-4" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-xs text-gray-500">
          <p><strong>Export TXT:</strong> Fichier texte simple avec toutes les réponses</p>
          <p><strong>Export HTML:</strong> Fichier HTML formaté avec statistiques et mise en page</p>
          <p><strong>Session ID:</strong> {sessionId}</p>
        </div>
      </Card>
    </div>
  );
}
